import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { RequisitionListItem, RequisitionDetail, fetchRequisitionDetails } from './requisitionSlice';
import { getStatusDisplay } from '../../utils/statusUtils';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatUtils';
import type { AuthUser } from '../auth/authSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../app/store';

interface RequisitionCardProps {
  requisition: RequisitionListItem;
  currentUser: AuthUser | null;
  isSelected?: boolean;
  onSelect?: (id: number) => void;
  onQuickView: (id: number) => void;
  onEdit?: (id: number) => void;
  onSubmit?: (id: number) => void;
  onResendNotification?: (id: number) => void;
  isResendingNotification?: boolean;
  refreshKey?: number;
}

// Component to preview items inline with auto-loading
interface ItemsPreviewProps {
  requisitionId: number;
  refreshKey?: number;
}

interface ProcessedItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  details?: string;
  descriptions?: string[];
  bgColor: string;
  borderColor: string;
  badgeColor: string;
}

// Cache for requisition details to avoid re-fetching (shared between card and table components)
export const sharedRequisitionDetailsCache = new Map<number, ProcessedItem[]>();

// Track loading states to prevent too many concurrent requests
export const loadingStates = new Set<number>();

const ItemsPreview: React.FC<ItemsPreviewProps> = ({ requisitionId, refreshKey }) => {
  const dispatch: AppDispatch = useDispatch();
  const [items, setItems] = useState<ProcessedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Ref for intersection observer
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer to detect when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: '50px' // Start loading 50px before the component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [hasLoaded]);

  // Reset hasLoaded when refreshKey changes to force re-fetch
  useEffect(() => {
    if (refreshKey !== undefined) {
      setHasLoaded(false);
      setItems([]);
      setExpanded(false);
      setShowAll(false);
    }
  }, [refreshKey]);

  // Auto-load items when component becomes visible
  useEffect(() => {
    if (isVisible && !hasLoaded && !loading) {
      // Check cache first
      const cachedItems = sharedRequisitionDetailsCache.get(requisitionId);
      if (cachedItems) {
        setItems(cachedItems);
        setExpanded(true); // Auto-expand when loaded
        setShowAll(false); // Start collapsed to preview
        setHasLoaded(true);
        return;
      }

      // Check if already loading this requisition
      if (loadingStates.has(requisitionId)) {
        return;
      }

      // Load from API
      setLoading(true);
      loadingStates.add(requisitionId);

      dispatch(fetchRequisitionDetails(requisitionId.toString()))
        .unwrap()
        .then((requisition: RequisitionDetail) => {
          const processedItems: ProcessedItem[] = [];

          // Process equipment items (same logic as QuickViewModal)
          if (requisition.equipmentItems && requisition.equipmentItems.length > 0) {
            requisition.equipmentItems.forEach((item, index) => {
              processedItems.push({
                id: `processed-eq-${index}`,
                type: 'Equipment',
                title: item.asset,
                subtitle: `Category: ${item.equipment.category} Subcategory: ${item.equipment.subcategory}`,
                details: `Serial Number: ${item.equipment.serial_number}`,
                descriptions: item.descriptions,
                bgColor: 'bg-amber-100 dark:bg-amber-900/70',
                borderColor: 'border-amber-200 dark:border-amber-700',
                badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200'
              });
            });
          }

          // Process asset description pairs
          if (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) {
            requisition.assetDescriptionPairs.forEach((pair, index) => {
              processedItems.push({
                id: `processed-asset-${index}`,
                type: 'Asset',
                title: `Asset: ${pair.asset}`,
                descriptions: pair.descriptions,
                bgColor: 'bg-indigo-100 dark:bg-indigo-900/70',
                borderColor: 'border-indigo-200 dark:border-indigo-700',
                badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
              });
            });
          }

          // Fallback to legacy data if no processed data
          if (processedItems.length === 0) {
            // Equipment requests
            if (requisition.equipmentRequests && requisition.equipmentRequests.length > 0) {
              requisition.equipmentRequests.forEach((item, index) => {
                processedItems.push({
                  id: `eq-${item.id || index}`,
                  type: 'Equipment',
                  title: item.equipment?.description || 'Equipment Item',
                  subtitle: item.equipment?.serial_number ? `S/N: ${item.equipment.serial_number}` : '',
                  details: item.notes ? `Notes: ${item.notes}` : '',
                  bgColor: 'bg-amber-100 dark:bg-amber-900/70',
                  borderColor: 'border-amber-200 dark:border-amber-700',
                  badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200'
                });
              });
            }

            // Assets with descriptions
            if (requisition.assets && requisition.assets.length > 0) {
              requisition.assets.forEach((item, index) => {
                const descriptions = item.descriptions?.map(desc => desc.description) || [];
                processedItems.push({
                  id: `asset-${item.id || index}`,
                  type: 'Asset',
                  title: `Asset: ${item.asset}`,
                  descriptions: descriptions,
                  bgColor: 'bg-indigo-100 dark:bg-indigo-900/70',
                  borderColor: 'border-indigo-200 dark:border-indigo-700',
                  badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
                });
              });
            }

            // Legacy repairs
            if (requisition.repairs && requisition.repairs.length > 0 && (!requisition.assets || requisition.assets.length === 0)) {
              requisition.repairs.forEach((repair, index) => {
                processedItems.push({
                  id: `legacy-repair-${repair.id || index}`,
                  type: 'Legacy',
                  title: `Item ${index + 1}`,
                  descriptions: [repair.description],
                  bgColor: 'bg-purple-100 dark:bg-purple-900/70',
                  borderColor: 'border-purple-200 dark:border-purple-700',
                  badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                });
              });
            }
          }

          // Cache the results
          sharedRequisitionDetailsCache.set(requisitionId, processedItems);

          setItems(processedItems);
          setExpanded(true); // Auto-expand when loaded
          setShowAll(false); // Start with preview (first 2 items)
          setHasLoaded(true);
          setLoading(false);
          loadingStates.delete(requisitionId);
        })
        .catch(() => {
          setLoading(false);
          setHasLoaded(true); // Mark as loaded even on error to prevent retry loops
          loadingStates.delete(requisitionId);
        });
    }
  }, [isVisible, hasLoaded, loading, requisitionId, dispatch, refreshKey]);

  return (
    <div className="mt-3" ref={containerRef}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        <svg 
          className={`w-4 h-4 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Items to Repair / Request 
        {!!loading && <span className="text-xs text-gray-500 ml-1">(Loading...)</span>}
        {items.length > 0 && !loading && <span className="ml-1">({items.length})</span>}
      </button>
      
      {!!loading && (
        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading items...
          </div>
        </div>
      )}
      
      {!!expanded && !!hasLoaded && !loading && (
        <div className="mt-2 space-y-2">
          {items.length > 0 ? (
            <>
              {(showAll ? items : items.slice(0, 2)).map((item) => (
                <div
                  key={item.id}
                  className={`p-2 border rounded-md ${item.bgColor} ${item.borderColor}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {item.title}
                      </p>
                      {!!item.subtitle && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {item.subtitle}
                        </p>
                      )}
                      {!!item.details && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {item.details}
                        </p>
                      )}
                      {!!item.descriptions && item.descriptions.length > 0 && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Description:</span>
                          </p>
                          <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                            {item.descriptions.map((desc: string, dIndex: number) => (
                              !!desc && <li key={dIndex}>{desc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${item.badgeColor}`}>
                      {item.type.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {items.length > 2 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  {showAll ? 'Show less' : `Show ${items.length - 2} more`}
                </button>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No items listed for repair or purchase.</p>
          )}
        </div>
      )}
    </div>
  );
};

const RequisitionCard: React.FC<RequisitionCardProps> = ({
  requisition,
  currentUser,
  isSelected = false,
  onSelect,
  onQuickView,
  onEdit,
  onSubmit,
  onResendNotification,
  isResendingNotification = false,
  refreshKey
}) => {
  // Debug logging removed
  
  // Determine if repair or new purchase
  const isRepair = (requisition as any).is_repair === 1;

  const { statusText, bgColor, textColor } = getStatusDisplay(requisition);

  // Determine job card count (supports both singular and array)
  const jobCardCount = (() => {
    const anyReq: any = requisition;
    if (Array.isArray(anyReq.jobCards)) return anyReq.jobCards.length;
    if (Array.isArray(anyReq.jobcards)) return anyReq.jobcards.length;
    if (Array.isArray(anyReq.job_cards)) return anyReq.job_cards.length;
    if (anyReq.order) {
      if (Array.isArray(anyReq.order.jobCards)) return anyReq.order.jobCards.length;
      if (Array.isArray(anyReq.order.jobcards)) return anyReq.order.jobcards.length;
      if (Array.isArray(anyReq.order.job_cards)) return anyReq.order.job_cards.length;
    }
    if (anyReq.jobCard) return 1;
    if (anyReq.job_card) return 1;
    return 0;
  })();

  // Determine if job card is required (when count is 0 but there's an order number)
  const isJobCardRequired = !!(jobCardCount === 0 && requisition.order?.order_number);

  // Updated canSelect logic for card
  const nextPendingApproverInCard = requisition.approvers?.find(app => app.status_id === 1);
  const canSelect = !!(
    currentUser &&
    requisition.submitted &&
    requisition.status_id === 1 &&
    nextPendingApproverInCard &&
    nextPendingApproverInCard.user_id === currentUser.id
  );

  // Edit/submit permission for unsubmitted creator/admin/coaches or pre-approval coaches
  const isCreatorOrAdmin = !!(currentUser && (currentUser.is_admin || currentUser.id === requisition.user?.id || currentUser.id === requisition.created_by));
  const isAreaOrRegionalCoach = !!(currentUser && (currentUser.user_role_id === 2 || currentUser.user_role_id === 3));
  const canEditUnsubmitted = (isCreatorOrAdmin || isAreaOrRegionalCoach) && requisition.status_id === 1 && !requisition.submitted;
  const canEditBeforeApproval = isAreaOrRegionalCoach && requisition.status_id === 1 && requisition.submitted;
  const canEdit = canEditUnsubmitted || canEditBeforeApproval;
  const canSubmit = (isCreatorOrAdmin || isAreaOrRegionalCoach) && requisition.status_id === 1 && !requisition.submitted;
  const canResendNotification = (isCreatorOrAdmin || isAreaOrRegionalCoach) && requisition.submitted && requisition.status_id === 1 && !!nextPendingApproverInCard && nextPendingApproverInCard.user_id !== currentUser?.id;

  return (
    <div className="block p-4 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg dark:hover:shadow-gray-700/50 transition-shadow duration-200 ease-in-out border border-gray-200 dark:border-gray-700 mb-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3">
        <div className="flex items-center">
          {!!canSelect && !!onSelect && (
            <div className="mr-3">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(requisition.id)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </div>
          )}
          <Link to={`/requisitions/${requisition.id}`} className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-1 sm:mb-0">
            ATR ID: {requisition.id} - {requisition.stores.map(s => `${s.name}${s.storenumber ? ` (${s.storenumber})` : ''}`).join(', ')}
          </Link>
        </div>
        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${bgColor} ${textColor}`}>
          {statusText}
        </span>
      </div>

      <div className="text-sm text-gray-700 dark:text-gray-300 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        <p><span className="font-medium text-gray-600 dark:text-gray-400">Category:</span> {requisition.category?.name || ((requisition as any).requisition_category_id === 1 ? 'Pots/Fryers/Toasters' : 'N/A')}{requisition.subcategory?.name ? ` - ${requisition.subcategory.name}` : ((requisition as any).requisition_sub_category_id === 19 ? ' - Toaster' : '')}</p>
        <p><span className="font-medium text-gray-600 dark:text-gray-400">Type:</span> <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">{isRepair ? 'Repair' : 'New Purchase'}</span></p>
        <p><span className="font-medium text-gray-600 dark:text-gray-400">Store</span> {requisition.user?.name || 'N/A'}</p>
        <p><span className="font-medium text-gray-600 dark:text-gray-400">Date:</span> {formatDate(requisition.date)}</p>
        <p><span className="font-medium text-gray-600 dark:text-gray-400">Last Updated:</span> {formatDateTime(requisition.updated_at)}</p>
        <p><span className="font-medium text-gray-600 dark:text-gray-400">
          {requisition.internal !== undefined ?
            (requisition.internal === 1 ? 'Technician:' : 'Supplier:') :
            'Supplier/Technician:'
          }
        </span> {
          requisition.internal !== undefined ?
            (requisition.internal === 1 ?
              (requisition.technician?.name || 'N/A') :
              (requisition.supplier?.name || 'N/A')
            ) : 'N/A'
        }</p>
        {requisition.quotation && (
          <p><span className="font-medium text-gray-600 dark:text-gray-400">Quotation:</span>
            <span className="text-green-600 dark:text-green-400 font-semibold ml-1">
              {formatCurrency(requisition.quotation.amount || 0)}
            </span>
          </p>
        )}
      </div>

      {/* Items Preview - replaces Quick View button */}
      <ItemsPreview requisitionId={requisition.id} refreshKey={refreshKey} />

      {!!requisition.submitted && requisition.status_id === 1 && requisition.approvers && requisition.approvers.length > 0 && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-medium">Next Approver:</span> {requisition.approvers.find(appr => appr.status_id === 1)?.user.name || 'N/A'}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        {/* Order Number Pill */}
        {(requisition.order?.order_number || requisition.order?.reference_number) && (
          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
            <span className="font-medium mr-1">{requisition.order?.order_number ? 'Order #:' : 'Ref #:'}</span>
            {requisition.order?.order_number ? (
              <Link to={`/purchase-orders/${requisition.order.id}`} className="hover:underline font-medium">
                {requisition.order.order_number}
              </Link>
            ) : (
              <span>{requisition.order?.reference_number}</span>
            )}
          </div>
        )}
        
        {/* Job Cards Pill */}
        {(jobCardCount > 0 || isJobCardRequired) && (
          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100">
            <span className="font-medium mr-1">Job Cards:</span>
            {jobCardCount > 0 ? jobCardCount : <span className="text-red-600 dark:text-red-500 font-semibold">Required</span>}
          </div>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 mt-3">
        {canEdit && onEdit && (
          <button
            onClick={() => onEdit(requisition.id)}
            className="px-3 py-1.5 text-sm font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            title={canEditUnsubmitted ? "Edit ATR" : "Edit ATR before approving"}
          >
            Edit
          </button>
        )}
        {canSubmit && onSubmit && (
          <button
            onClick={() => onSubmit(requisition.id)}
            className="px-3 py-1.5 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
            title="Submit ATR for approval"
          >
            Submit
          </button>
        )}
        {!!canResendNotification && onResendNotification && (
          <button
            onClick={() => onResendNotification(requisition.id)}
            disabled={isResendingNotification}
            className="px-3 py-1.5 text-sm font-semibold rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            title={`Resend notification to ${nextPendingApproverInCard?.user?.name || 'next approver'}`}
          >
            {isResendingNotification && (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isResendingNotification ? 'Sending...' : 'Resend'}
          </button>
        )}
        <Link to={`/requisitions/${requisition.id}`} className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 text-sm font-medium">
          View Details &rarr;
        </Link>
      </div>
    </div>
  );
};

export default RequisitionCard;
